import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAgents } from "@/components/Agents/AgentsContext";
import { agentsHref } from "@/lib/atwAgentsHost";

export function ConnectionsEmailPage() {
  const { org, may, viewOnly } = useAgents();
  const navigate = useNavigate();
  const canManage = may("manage_connections");

  const connectionName = (connectionId: string) =>
    org.connections.find((conn) => conn.id === connectionId)?.name ?? connectionId;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Group / Shared inboxes
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
            Shared inboxes are team addresses for the organization. Agents attach to them. This is
            not sharing a person&apos;s personal email. Mail pipes still come from Connections
            (ATW Mail, Google, or Microsoft).
          </p>
        </div>
        <Button
          type="button"
          disabled={!canManage}
          onClick={() => navigate(agentsHref("/connections/email/new"))}
          title={
            viewOnly
              ? "Standard role is view only. Ask a Super Admin to add a shared inbox."
              : "Add a shared inbox"
          }
        >
          <Plus className="size-4" aria-hidden />
          Shared inbox
        </Button>
      </div>

      {viewOnly ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          Standard role is view only. Ask a Super Admin to add a shared inbox.
        </p>
      ) : null}

      {org.sharedInboxes.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">No shared inboxes yet</CardTitle>
            <CardDescription>
              Add a team address here, then agents can attach to it under Settings Email.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              disabled={!canManage}
              onClick={() => navigate(agentsHref("/connections/email/new"))}
            >
              Add a shared inbox
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {org.sharedInboxes.map((inbox) => (
            <Card key={inbox.id}>
              <CardHeader>
                <CardTitle className="text-base">{inbox.name}</CardTitle>
                <CardDescription>{inbox.emailAddress}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm text-muted-foreground">
                <p>Mail connection: {connectionName(inbox.connectionId)}</p>
                {inbox.note ? <p>{inbox.note}</p> : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        Need a mail pipe first?{" "}
        <Link to={agentsHref("/connections")} className="underline underline-offset-2">
          Open Connections
        </Link>
        .
      </p>
    </div>
  );
}
